import React, { useState } from 'react';
import useComments from '@/hooks/useComments';
import { formatDate } from '@utils/Date';

/**
 * Reusable Comments panel with an inline toggle button.
 *
 * Props:
 * - parentId (string)               : required – document id of the parent item
 * - collectionPath (string)         : required – top-level collection (e.g., "adverts" | "memorials")
 * - globalUser (object)             : required – current user object (for addComment)
 * - defaultAvatarUrl (string)       : optional – fallback avatar
 * - parentIdNull (boolean)          : optional – pass through to your hook if needed
 * - className (string)              : optional – container class
 * - collapsedByDefault (boolean)    : optional – start closed (default: true)
 * - showHeader (boolean)            : optional – show header row (default: true)
 */
function Comments({
  parentId,
  collectionPath,
  globalUser,
  defaultAvatarUrl,
  parentIdNull = false,
  className = '',
  collapsedByDefault = true,
  showHeader = true,
  showCommentLabel = true,
}) {
  const { comments, loading, addComment } = useComments(
    parentId,
    collectionPath,
    globalUser,
    defaultAvatarUrl,
    parentIdNull
  );

  const [isOpen, setIsOpen] = useState(!collapsedByDefault);
  const [newComment, setNewComment] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    await addComment(newComment.trim());
    setNewComment('');
  };

  return (
    <div className={`${className}`}>
      {/* Header / Toggle */}
      {showHeader && (
        <div className='col-12 d-flex align-items-center mb-2'>
          <div
            className='tooltip-container'
            style={{ display: 'inline-block', marginRight: '10px' }}
          >
            <button
              type='button'
              className={[
                'reaction-btn',
                'reaction-comment',
                isOpen ? 'is-active' : '',
              ].join(' ')}
              title={isOpen ? 'Hide comments' : 'Show comments'}
              onClick={
                showCommentLabel ? () => setIsOpen((v) => !v) : undefined
              }
            >
              {/* FontAwesome comments icon */}
              <i className='fas fa-comments reaction-icon' />
            </button>
            <span className='tooltip'>
              {showCommentLabel ? (
                isOpen ? (
                  'Hide comments'
                ) : (
                  'Show comments'
                )
              ) : (
                <>
                  {comments?.length ?? 0}{' '}
                  {comments?.length === 1 ? 'comment' : 'comments'}
                </>
              )}
            </span>
          </div>
          {showCommentLabel && (
            <span className='ms-1 small text-muted'>
              {comments?.length ?? 0}{' '}
              {comments?.length === 1 ? 'comment' : 'comments'}
            </span>
          )}
        </div>
      )}

      {/* Panel */}
      {isOpen && (
        <div className='card border shadow-none card-body text-muted mb-0'>
          {loading ? (
            <p className='mb-3'>Loading comments...</p>
          ) : (comments || []).length ? (
            comments.map((comment) => (
              <div key={comment.id} className='d-flex align-items-center mb-3'>
                <img
                  src={comment.user?.avatar || defaultAvatarUrl}
                  className='avatar-sm rounded-circle me-3'
                  style={{
                    width: '48px',
                    height: '48px',
                    objectFit: 'cover',
                  }}
                  alt='avatar'
                />
                <div>
                  <p className='mb-1'>
                    <strong>{comment.user?.name || 'Unknown User'}</strong>
                    <br />
                    {comment.text}
                  </p>
                  {comment.createdAt?.toDate && (
                    <small className='text-muted'>
                      {formatDate(comment.createdAt.toDate(), 'long')}
                    </small>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className='mb-3'>No comments yet.</p>
          )}

          {/* Add comment */}
          <form onSubmit={handleSubmit}>
            <div className='input-group mt-2'>
              <input
                type='text'
                className='form-control btn-xs'
                placeholder='Add a comment...'
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                required
              />
              <button type='submit' className='btn btn-primary btn-xs'>
                Submit
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default Comments;
